/*
 * -----------------------------------------------------------------------
 *  <copyright file="data-permissions-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { Checkbox } from '@progress/kendo-react-inputs';
import { useTranslation, useGridCells } from 'hooks';
import { IPermission } from 'interfaces/permission.interface';
import { OrganisationRoleFields_OrganisationRole_rights_OrganisationRoleRight } from 'queries/generated/fragments.generated';
import ExpandableGrid from 'views/components/core/expandable/grid';

import './styles.scss';

const DataPermissionsGrid = ({ role, updatePermissions, possiblePermissions, isEditable }: any) => {
    const { t } = useTranslation();
    const { CustomizableCell } = useGridCells();

    const [permissions, setPermissions] = useState([]);

    useEffect(() => {
        if (role?.rights?.length && possiblePermissions?.length) {
            const array = possiblePermissions.map((item: any) => {
                const element = role.rights.find(
                    (el: OrganisationRoleFields_OrganisationRole_rights_OrganisationRoleRight) =>
                        el.element === item.element,
                );
                return {
                    ...item,
                    permissions: element?.permissions ?? 0,
                };
            });
            setPermissions(array);
        } else {
            const array = possiblePermissions.map((item: any) => ({
                ...item,
            }));
            setPermissions(array);
        }
    }, [role]);

    const handlePermissions = (item: IPermission, value: number) => {
        const array = [...permissions];
        const index = array.findIndex((el: IPermission) => el.element === item.element);
        const element = array[index];
        element.permissions = value;
        setPermissions(array);
        updatePermissions(array);
    };

    return (
        <>
            {!role && <p className="no-role-selected">{t('uam:selectRoleToCustomise')}</p>}
            {role && (
                <ExpandableGrid
                    customKey="data-permissions-grid"
                    items={permissions}
                    showPager
                    groupable={false}
                    scrollable="scrollable"
                    reorderable
                    pageable={false}
                    resizable
                    showFirstColumn={false}
                    addScroll
                    options={{
                        allowExcelExport: false,
                        allowExpandCollapseAll: false,
                        useFilterChips: false,
                    }}
                    detail={null}
                    columns={[
                        {
                            field: 'title',
                            id: 'module-title',
                            label: t('uam:dataPermission'),
                            minWidth: 230,
                        },
                        {
                            label: t('hide'),
                            field: 'permissions',
                            id: 'permission-hide',
                            minWidth: 130,
                            cells: {
                                data: CustomizableCell((dataItem: any) =>
                                    dataItem.showHide ? (
                                        <Checkbox
                                            rounded="none"
                                            disabled={!isEditable}
                                            checked={!dataItem.permissions}
                                            onChange={(event: any) => {
                                                handlePermissions(dataItem, event.value ? 0 : 1);
                                            }}
                                            data-testid="hide-checkbox"
                                            size="medium"
                                        />
                                    ) : null,
                                ),
                            },
                        },
                        {
                            label: t('View'),
                            field: 'permissions',
                            id: 'permission-show',
                            minWidth: 130,
                            cells: {
                                data: CustomizableCell((dataItem: any) =>
                                    dataItem.showHide ? (
                                        <Checkbox
                                            rounded="none"
                                            disabled={!isEditable}
                                            checked={dataItem.permissions}
                                            onChange={(event: any) => {
                                                handlePermissions(dataItem, event.value ? 1 : 0);
                                            }}
                                            data-testid="show-checkbox"
                                            size="medium"
                                        />
                                    ) : null,
                                ),
                            },
                        },
                        {
                            label: t('readOnly'),
                            field: 'permissions',
                            id: 'permission-read-only',
                            minWidth: 160,
                            cells: {
                                data: CustomizableCell((dataItem: any) =>
                                    !dataItem.showHide ? (
                                        <Checkbox
                                            rounded="none"
                                            disabled={!isEditable}
                                            checked={!dataItem.permissions}
                                            onChange={(event: any) => {
                                                handlePermissions(dataItem, event.value ? 0 : 1);
                                            }}
                                            data-testid="readonly-checkbox"
                                            size="medium"
                                        />
                                    ) : null,
                                ),
                            },
                        },
                        {
                            label: t('readWrite'),
                            field: 'permissions',
                            id: 'permission-read-write',
                            minWidth: 160,
                            cells: {
                                data: CustomizableCell((dataItem: any) =>
                                    !dataItem.showHide ? (
                                        <Checkbox
                                            rounded="none"
                                            disabled={!isEditable}
                                            checked={dataItem.permissions}
                                            onChange={(event: any) => {
                                                handlePermissions(dataItem, event.value ? 1 : 0);
                                            }}
                                            data-testid="read-write-checkbox"
                                            size="medium"
                                        />
                                    ) : null,
                                ),
                            },
                        },
                    ]}
                />
            )}
        </>
    );
};

export default DataPermissionsGrid;
